import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { putReq, getReq, delReq } from "../../configs/ApiUrl.js";
import { Modal, message } from "antd";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n.js";
import Button from "../Button.js";
import {
  Paper,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

function UserContact() {
  const { t, i18n } = useTranslation(["userProfile", "forms"]);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    username: "",
    phone: "",
    street: "",
    city: "",
    postalCode: "",
    state: "",
    userId: "",
    companyName: "",
  });
  const [loading, setLoading] = useState(false);
  const [isSubAdmin, setIsSubAdmin] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState("en");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      setLoading(true);
      try {
        const response = await getReq("/user/profile");
        if (response.success && response.user) {
          const {
            email,
            username,
            phone,
            address,
            _id,
            companyName,
            role,
            preferredLanguage,
          } = response.user;
          setFormData({
            email,
            username,
            phone: phone || "",
            street: address?.street || "",
            city: address?.city || "",
            postalCode: address?.postalCode || "",
            state: address?.state || "",
            userId: _id,
            companyName: companyName || "",
            preferredLanguage: preferredLanguage || "en",
          });
          await i18n.changeLanguage(preferredLanguage || "en");
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLanguageChange = async (e) => {
    const selectedLanguage = e.target.value;
    setPreferredLanguage(selectedLanguage);
    setFormData({ ...formData, preferredLanguage: selectedLanguage });
    try {
      await i18n.changeLanguage(selectedLanguage);
      const updateResp = await putReq("/user/update", {
        userId: formData.userId,
        preferredLanguage: selectedLanguage,
      });
    } catch (error) {
      console.error("Error updating language:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { street, postalCode, city } = formData;
    const postalCodePattern = /^\d{3} \d{2}$/;
    const streetPattern = /^[a-zA-ZåäöÅÄÖ\s]+ \d+[a-zA-Z]?$/;
    const cityPattern = /^[a-zA-ZåäöÅÄÖ\s-]+$/;
    if (
      !postalCodePattern.test(postalCode) ||
      !streetPattern.test(street) ||
      !cityPattern.test(city)
    )
      return;

    setLoading(true);
    try {
      const updatedData = {
        email: formData.email,
        username: formData.username,
        phone: formData.phone,
        address: {
          street: formData.street,
          city: formData.city,
          postalCode: formData.postalCode,
          state: formData.state,
        },
        userId: formData.userId,
        preferredLanguage,
      };
      if (isSubAdmin) {
        updatedData.companyName = formData.companyName;
      }
      await putReq("/user/update", updatedData);
      message.success(t("userProfile:updateSuccessful"));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      const deleteResp = await delReq("/user/delete");
      if (deleteResp?.success) navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  const fields = [
    { label: t("forms:emailLabel"), name: "email", type: "email" },
    { label: t("forms:usernameLabel"), name: "username", type: "text" },
    isSubAdmin && {
      label: t("forms:companyNameLabel"),
      name: "companyName",
      type: "text",
    },
    { label: t("forms:phoneLabel"), name: "phone", type: "tel" },
    { label: t("forms:streetLabel"), name: "street", type: "text" },
    { label: t("forms:cityLabel"), name: "city", type: "text" },
    { label: t("forms:postalLabel"), name: "postalCode", type: "text" },
    { label: t("forms:provinceLabel"), name: "state", type: "text" },
    {
      label: t("forms:langPref"),
      name: "preferredLanguage",
      type: "select",
      options: [
        { value: "en", label: "English" },
        { value: "sv", label: "Swedish" },
      ],
    },
  ].filter(Boolean);

  return (
    <Grid
      container
      justifyContent='center'
      >
      <Grid
        item
        xs={12}
        md={8}>
        <Paper
          elevation={3}
          sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Grid
              container
              spacing={3}>
              {fields.map((field, index) => (
                <Grid
                  item
                  xs={12}
                  sm={field.colSpan === 2 ? 12 : 6}
                  key={index}>
                  {field.type === "select" ? (
                    <FormControl fullWidth>
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        value={formData.preferredLanguage}
                        label={field.label}
                        onChange={handleLanguageChange}>
                        {field.options.map((option) => (
                          <MenuItem
                            value={option.value}
                            key={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label={field.label}
                      name={field.name}
                      type={field.type}
                      value={formData[field.name]}
                      onChange={handleChange}
                      disabled={field.name === "email"}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
            <Grid
              container
              justifyContent='center'
              sx={{ mt: 4 }}>
              <Button
                type='submit'
                variant='main'
                size='lg'>
                {t("forms:profileUpdateButton")}
              </Button>
            </Grid>
          </form>
          <Grid
            container
            justifyContent='center'
            sx={{ mt: 2 }}>
            <Typography
              onClick={() => setShowDeleteModal(true)}
              sx={{ color: "error.main", cursor: "pointer" }}>
              {t("forms:deleteAccountButton")}
            </Typography>
          </Grid>
        </Paper>
      </Grid>

      <Modal
        title={t("forms:deleteAccountButton")}
        visible={showDeleteModal}
        onOk={handleDeleteAccount}
        onCancel={() => setShowDeleteModal(false)}
        confirmLoading={loading}
        okText='Yes, Delete'
        cancelText='Cancel'
        className='delete-account-modal'
        okButtonProps={{
          className: "delete-btn-ok",
        }}>
        <p>{t("forms:deleteAccountText")}</p>
      </Modal>
    </Grid>
  );
}

export default UserContact;
